import React from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import { useUploadHistoryStore } from '../../hooks/useUploadHistoryStore';
import { useAuth } from '../../contexts/AuthContext';
import CloseIcon from '../icons/CloseIcon';
import Row from '../ui/core/Row';
import { formatInstant } from '../helpers/dataHelper';

// Statuses whose files can be re-ingested (anything that is not fully PROCESSED).
const REPROCESSABLE_STATUSES = ['SKIPPED', 'ERROR', 'PARTIALLY_PROCESSED'];

const FileDetailViewer: React.FC = observer(() => {
  const store = useUploadHistoryStore();
  const { isAdmin } = useAuth();
  const { selectedFile, isLoading, isReprocessing, reprocessMessage } = store;

  const canReprocess =
    isAdmin && !!selectedFile && REPROCESSABLE_STATUSES.includes(selectedFile.status);

  const handleReprocess = async () => {
    if (!selectedFile) return;
    await store.reprocessFile(selectedFile.id);
  };

  // File detail is loaded when row is clicked via openFileDetail

  const getLogTypeColor = (type: string) => {
    switch (type) {
      case 'UPLOAD':
        return { bgColor: '#DBEAFE', textColor: '#1E40AF' }; // Blue
      case 'PROCESSING':
        return { bgColor: '#FEF3C7', textColor: '#92400E' }; // Yellow
      case 'CLAIM':
      case 'COMPLETE':
        return { bgColor: '#DCFCE7', textColor: '#166534' }; // Green
      case 'SKIP':
        return { bgColor: '#FFEDD5', textColor: '#9A3412' }; // Orange
      case 'ERROR':
        return { bgColor: '#FEE2E2', textColor: '#991B1B' }; // Red
      default:
        return { bgColor: '#F3F4F6', textColor: '#374151' }; // Gray
    }
  };

  const getLogTypeBorderColor = (type: string) => {
    switch (type) {
      case 'UPLOAD':
        return '#1E40AF'; // Blue
      case 'PROCESSING':
        return '#92400E'; // Yellow
      case 'CLAIM':
      case 'COMPLETE':
        return '#166534'; // Green
      case 'SKIP':
        return '#9A3412'; // Orange
      case 'ERROR':
        return '#991B1B'; // Red
      default:
        return '#6B7280'; // Gray
    }
  };

  const formatFileHash = (hash: string) => {
    if (!hash || hash.length <= 8) return hash;
    return `${hash.substring(0, 4)}...${hash.substring(hash.length - 4)}`;
  };

  const handleClose = () => {
    store.closeFileDetail();
  };

  if (!selectedFile) {
    return null;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          backgroundColor: '#f9fafb'
        }}
      >
        <Row sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
            {selectedFile.file_name}
          </Typography>
          <Chip
            label={selectedFile.status}
            size="small"
            sx={{
              backgroundColor: getLogTypeColor(selectedFile.status).bgColor,
              color: getLogTypeColor(selectedFile.status).textColor,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '4px',
            }}
          />
        </Row>
        {canReprocess && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleReprocess}
            disabled={isReprocessing}
            startIcon={isReprocessing ? <CircularProgress size={14} /> : undefined}
            sx={{ ml: 2, flexShrink: 0, textTransform: 'none' }}
          >
            {isReprocessing ? 'Reprocessing…' : 'Reprocess'}
          </Button>
        )}
        <IconButton
          onClick={handleClose}
          sx={{ ml: 1, flexShrink: 0 }}
          size="small"
        >
          <CloseIcon className="w-5 h-5" />
        </IconButton>
      </Box>
      {reprocessMessage && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#f1f5f9',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {reprocessMessage}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          backgroundColor: '#f3f4f6'
        }}
      >
        {isLoading && !selectedFile ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedFile ? (
          <Box sx={{backgroundColor: 'white', padding: '0.75rem'}}>
            {/* Metadata Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 600 }}>
                File Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    File Hash
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                    {formatFileHash(selectedFile.file_hash)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    EDI Type
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {selectedFile.edi_object_type || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Uploaded By
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {selectedFile.uploaded_by || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Date Uploaded
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatInstant(selectedFile.uploaded_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Claims Found
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {selectedFile.claims_found}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Claims Processed
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {selectedFile.claims_processed}
                  </Typography>
                </Box>
                {selectedFile.date_submitted && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      Date Submitted
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedFile.date_submitted}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Timeline Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 600 }}>
                Processing Timeline
              </Typography>
              {selectedFile.details && selectedFile.details.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {selectedFile.details.map((detail, index) => {
                    const colors = getLogTypeColor(detail.type);
                    return (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.813rem', fontWeight: 500 }}>
                            {formatInstant(detail.time)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              label={detail.type}
                              size="small"
                              sx={{
                                backgroundColor: colors.bgColor,
                                color: colors.textColor,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: '20px',
                                borderRadius: '4px',
                              }}
                            />
                            {detail.claim_number && detail.type !== 'CLAIM' && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {detail.claim_number}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.813rem', pb: 1 }}>
                          {detail.msg}
                        </Typography>
                        {index < selectedFile.details.length - 1 && (
                          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', my: 0.5 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No processing details available
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">No file selected</Typography>
        )}
      </Box>
    </Box>
  );
});

export default FileDetailViewer;

